import env, { SiteId } from '@/env';
import isNil from 'lodash/isNil';
import createAuthorizedApiClient, { AuthTokenType } from '../authorized-api-client';
import authHelpers from '../auth/helpers';

export interface ApiDocForm {
  apiCategory: string[];
  groupName: string;
  apiName: string;
  description: string;
  iconName: string;
  apiImage: File | string;
  publisher: string;
  isPublic: boolean;
  showApiOverview: boolean;
  tags: string[];
  swagger: File | string;
  complianceLevels: string[];
}

export interface OrganisationForm {
  name: string;
  shortName: string;
  legalName: string;
  subdomain: string;
  contact: string;
  entityType: string;
  isPublic: boolean;
  isAuthenticatedOnly: boolean;
  logo?: File | string;
  locCountry?: string;
  locCountryCode?: string;
  locRegion?: string;
  description?: string;
  apiPortalLink?: string;
  webSiteLink?: string;
  linkedIn?: string;
  facebook?: string;
  twitter?: string;
  google?: string;
  contactUs?: string;
  aboutUs?: string;
  privacyPolicy?: string;
  termsAndConditions?: string;
  faq?: string;
  primaryColor?: string;
  accentColor?: string;
  showProcess: boolean;
  showTestimonials: boolean;
  showNews: boolean;
  showPartnerships: boolean;
  showShowcase: boolean;
  showSubscribe: boolean;
  showHeaderName: boolean;
  showApiOverview?: boolean;
  isOpenBankingCompliance?: boolean;
  dataSource?: string;
}

const apiClient = createAuthorizedApiClient(
  env.api.baseUrl.apiPortal,
  AuthTokenType.AppOrUserAccessToken,
  true,
);

class ApiPortalService {
  searchApiDocs = async (apiName: string) => {
    const response = await apiClient.get(`api-docs/search?apiName=${apiName}`);
    return response.data;
  };

  fetchApiDocs = async (
    organisationId: string,
    pageNumber?: number,
    pageSize?: number,
    searchText?: string,
    groupName?: string,
  ) => {
    const response = await apiClient.get('apis', {
      headers: {
        organisationId,
      },
      params: {
        pageNumber,
        pageSize,
        searchText,
        groupName,
      },
    });
    return response.data;
  };

  fetchApiDoc = async (apiId: string, organisationId: string) => {
    const response = await apiClient.get(`apis/${apiId}`, {
      headers: {
        organisationId,
      },
    });
    return response.data;
  };

  fetchApiGroups = async (organisationId: string) => {
    const response = await apiClient.get(`apis/api-groups`, {
      headers: {
        organisationId,
      },
    });
    return response.data;
  };

  deleteApiDoc = async (id: string, organisationId: string) => {
    const response = await apiClient.delete(`apis/${id}`, {
      headers: { organisationId },
    });

    return response.data;
  };

  createApiDoc = async (apiDoc: ApiDocForm, organisationId: string) => {
    const formData = this.formDataFromApiDoc(apiDoc);

    const response = await apiClient.post('apis', formData, {
      headers: { 'Content-Type': 'multipart/form-data', organisationId },
    });

    return response.data;
  };

  updateApiDoc = async (id: string, apiDoc: ApiDocForm, organisationId: string) => {
    const formData = this.formDataFromApiDoc(apiDoc);
    const response = await apiClient.put(`apis/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', organisationId },
    });

    return response.data;
  };

  voteApiDoc = async (docId: string) => {
    const response = await apiClient.post(`api-docs/vote/${docId}`);
    return response.data;
  };

  rateApiDoc = async (docId: string, rating: number) => {
    const response = await apiClient.post(`apis/${docId}/rate`, { rating });
    return response.data;
  };

  fetchMetadata = async (domain: string) => {
    const response = await apiClient.get(`entities/subdomain/${domain}`);
    return response.data;
  };

  toggleEntityEnabled = async (entityId: string, isActive: boolean) => {
    await apiClient.post(`entities/enabled/${entityId}`, { status: isActive.toString() });
  };

  createOrganisation = async (organisation: OrganisationForm) => {
    const formData = this.formDataFromOrganisation(organisation, env.siteId);

    const response = await apiClient.post('entities', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  };

  updateOrganisation = async (organisationId: string, organisation: OrganisationForm) => {
    const formData = this.formDataFromOrganisation(organisation, env.siteId);
    const response = await apiClient.put('entities', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        organisationId,
      },
    });

    return response.data;
  };

  fetchOrganisation = async (organisationId: string) => {
    const response = await apiClient.get(`entities/${organisationId}`);
    return response.data;
  };

  fetchOrganisationApiKey = async (organisationId: string) => {
    const response = await apiClient.get(`entities/${organisationId}/api-key`);
    return response.data;
  };

  getDefaultApis = async (organisationId: string) => {
    const accessToken = authHelpers.getAccessToken();
    const body = { accessToken, organisationId };
    const response = await apiClient.post('entities/defaults', body);
    return response.data;
  };

  reloadDefaultEntity = async (organisationId: string, type: string) => {
    const accessToken = authHelpers.getAccessToken();
    const body = { accessToken, type };
    const response = await apiClient.post('entities/defaults/reload', body, {
      headers: {
        'Content-Type': 'application/json',
        organisationId,
      },
    });
    return response.data;
  };

  private formDataFromApiDoc = (apiDoc: ApiDocForm) => {
    const formData = new FormData();

    if (typeof apiDoc.swagger === 'string') {
      formData.append('swaggerUrl', apiDoc.swagger);
    } else {
      formData.append('swagger', apiDoc.swagger);
    }
    formData.append('groupName', apiDoc.groupName);
    env.siteId === SiteId.BankApi &&
      formData.append('complianceLevels', apiDoc.complianceLevels.join(','));
    formData.append('apiCategory', apiDoc.apiCategory.join(','));
    formData.append('apiName', apiDoc.apiName);
    formData.append('description', apiDoc.description);
    formData.append('iconName', apiDoc.iconName);
    formData.append('apiImage', apiDoc.apiImage);
    formData.append('tags', apiDoc.tags.join(','));
    formData.append('isPublic', apiDoc.isPublic?.toString());
    formData.append('showApiOverview', apiDoc.showApiOverview?.toString());
    return formData;
  };

  private formDataFromOrganisation = (organisation: OrganisationForm, appId: string) => {
    const formData = new FormData();
    formData.append('appId', appId);
    formData.append('name', organisation.name);
    formData.append('shortName', organisation.name);
    formData.append('legalName', organisation.legalName ?? '');
    formData.append('subdomain', organisation.subdomain ?? '');
    formData.append('contact', organisation.contact ?? '');
    formData.append('isPublic', organisation.isPublic.toString());
    formData.append('isAuthenticatedOnly', organisation.isAuthenticatedOnly.toString());
    env.siteId === SiteId.BankApi && formData.append('entityType', organisation.entityType ?? '');
    formData.append('locCountry', organisation.locCountry ?? '');
    formData.append('locCountryCode', organisation.locCountryCode ?? '');
    formData.append('locRegion', organisation.locRegion ?? '');
    formData.append('description', organisation.description ?? '');
    formData.append('linkedIn', organisation.linkedIn ?? '');
    formData.append('apiPortalLink', organisation.apiPortalLink ?? '');
    formData.append('webSiteLink', organisation.webSiteLink ?? '');
    formData.append('google', organisation.google ?? '');
    formData.append('twitter', organisation.twitter ?? '');
    formData.append('facebook', organisation.facebook ?? '');
    formData.append('contactUs', organisation.contactUs ?? '');
    formData.append('aboutUs', organisation.aboutUs ?? '');
    formData.append('privacyPolicy', organisation.privacyPolicy ?? '');
    formData.append('termsAndConditions', organisation.termsAndConditions ?? '');
    formData.append('faq', organisation.faq ?? '');
    formData.append('primaryColor', organisation.primaryColor ?? '');
    formData.append('accentColor', organisation.accentColor ?? '');
    formData.append('showProcess', organisation.showProcess.toString());
    formData.append('showTestimonials', organisation.showTestimonials.toString());
    formData.append('showNews', organisation.showNews.toString());
    formData.append('showPartnerships', organisation.showPartnerships.toString());
    formData.append('showShowcase', organisation.showShowcase.toString());
    formData.append('showSubscribe', organisation.showSubscribe.toString());
    formData.append('showHeaderName', organisation.showHeaderName.toString());
    env.siteId === SiteId.BankApi &&
      !isNil(organisation.isOpenBankingCompliance) &&
      formData.append('isOpenBankingCompliance', organisation.isOpenBankingCompliance.toString());
    organisation.dataSource && formData.append('dataSource', organisation.dataSource);
    !isNil(organisation.showApiOverview) &&
      formData.append('showApiOverview', organisation.showApiOverview.toString() ?? '');
    if (organisation.logo) {
      formData.append('logo', organisation.logo);
    }
    return formData;
  };
}

const instance = new ApiPortalService();
export default instance;
